// src/components/Events/EventActionButtons.js - WITH LANGUAGE SWITCHING (FIXED LAYOUT)
import React, { useState, useCallback, useEffect } from 'react';
import { Tooltip, Menu, MenuItem, Snackbar, Alert, IconButton, Box } from '@mui/material';
import { styled } from '@mui/system';
import ShareIcon from '@mui/icons-material/Share';
import LinkIcon from '@mui/icons-material/Link';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { onExplicitUiLangSwitch } from '../../services/langPrefs';
import { useLanguage } from '../../contexts/LanguageContext';

// Import flag images (same as EventDetailHeader)
import ThaiFlag from './thai-flag.png';
import EnglishFlag from './english-flag.png';
import ChinaFlag from './china-flag.png';

// Import the simplified participation button component
import ParticipationButton from '../common/ParticipationButton.js';

// Import styled components with fallback - UPDATED to use proper flexbox layout
let ActionButtonsContainer;
try {
  const styles = require('./styles');
  ActionButtonsContainer = styles.ActionButtonsContainer;
} catch (error) {
  console.warn('Styled components not found, using fallback styles');
  // Fallback if styled components don't exist - FIXED LAYOUT
  ActionButtonsContainer = ({ children }) => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between',
      alignItems: 'center',
      mt: 2,
      flexWrap: 'wrap',
      gap: 1
    }}>
      {children}
    </Box>
  );
}

// Import auth context with fallback
let useAuth;
try {
  const authModule = require('../Login/AuthContext');
  useAuth = authModule.useAuth;
} catch (error) {
  console.warn('AuthContext not found, using fallback auth');
  // Fallback if auth context doesn't exist
  useAuth = () => ({
    isAuthenticated: !!localStorage.getItem('token'),
    currentUser: { id: 1 }
  });
}

// Styled language button (same as EventDetailHeader)
const FlagButton = styled(IconButton)(({ isActive, theme }) => ({
  padding: '4px',
  border: isActive ? '2px solid #3a7bd5' : '2px solid transparent',
  borderRadius: '50%',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

// API URL configuration
const API_URL = process.env.REACT_APP_API_URL ;

const EventActionButtons = ({
  eventId,
  eventTitle,
  eventData,
  onParticipationChange,
  getAuthHeaders
}) => {
  const navigate = useNavigate();
  const { changeLanguage } = useLanguage();

  // Get auth state from context (or fallback)
  const { isAuthenticated } = useAuth();

  // Share menu state
  const [anchorShareMenu, setAnchorShareMenu] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  // Language versions state
  const [languageVersions, setLanguageVersions] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState(eventData?.language || 'EN');
  const [loadingLanguages, setLoadingLanguages] = useState(false);

  // Language options (same as EventDetailHeader)
  const languageOptions = [
    { code: 'TH', flag: ThaiFlag },
    { code: 'EN', flag: EnglishFlag },
    { code: 'CN', flag: ChinaFlag }
  ];

  // Fetch available language versions of this event
  const fetchLanguageVersions = useCallback(async () => {
    console.log('🔍 fetchLanguageVersions called with:', {
      eventId,
      eventData: eventData ? 'exists' : 'null',
      master_event_id: eventData?.master_event_id,
      language: eventData?.language
    });
    
    if (!eventId || !eventData?.master_event_id) {
      console.log('⚠️ Skipping language versions fetch - missing eventId or master_event_id');
      return;
    }
    
    setLoadingLanguages(true);
    try {
      console.log(`🌐 Fetching language versions for master event ${eventData.master_event_id}`);
      
      const response = await axios.get(
        `${API_URL}/events/${eventData.master_event_id}/language-versions`,
        getAuthHeaders ? getAuthHeaders() : {}
      );
      
      console.log('📡 API Response:', response.data);
      
      if (response.data && Array.isArray(response.data)) {
        setLanguageVersions(response.data);
        console.log(`✅ Found ${response.data.length} language versions:`, response.data);
        console.log('🎯 hasLanguageVersions will be:', response.data.length > 0);
      } else {
        console.log('⚠️ Invalid response format:', response.data);
        setLanguageVersions([]);
      }
    } catch (error) {
      console.error('❌ Error fetching language versions:', error);
      console.log('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        url: `${API_URL}/events/${eventData.master_event_id}/language-versions`
      });
      // Don't show error to user, just log it
      setLanguageVersions([]);
    } finally {
      setLoadingLanguages(false);
    }
  }, [eventId, eventData, getAuthHeaders]); // Fixed dependency warning

  // Load language versions when component mounts or eventData changes
  useEffect(() => {
    if (eventData) {
      setCurrentLanguage(eventData.language || 'EN');
      fetchLanguageVersions();
    }
  }, [eventData, fetchLanguageVersions]);

  // Handle language change
  const handleLanguageChange = useCallback((targetLanguage) => {
    if (targetLanguage === currentLanguage) {
      return; // Already on this language
    }

    // Find the event version for the target language
    const targetVersion = languageVersions.find(version => version.language === targetLanguage);

    if (targetVersion && targetVersion.id) {
      console.log(`🌐 Switching to ${targetLanguage} version (Event ID: ${targetVersion.id})`);

      // 1) set global main fetch language first
      changeLanguage(targetLanguage);
      onExplicitUiLangSwitch(targetLanguage);
      // 2) then navigate to language-specific event
      navigate(`/event/${targetVersion.id}`);

      // Show success message
      setSnackbarMessage(`Switched to ${targetLanguage} version`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } else {
      console.log(`⚠️ No ${targetLanguage} version available for this event`);

      // Show info message
      setSnackbarMessage(`${targetLanguage} version not available for this event`);
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
    }
  }, [currentLanguage, languageVersions, navigate, changeLanguage]);

  // Handle participation status changes
  const handleParticipationChange = useCallback((newStatus, result) => {
    console.log('Participation changed:', newStatus, result);
    
    // Update parent component
    if (onParticipationChange) {
      onParticipationChange(newStatus);
    }

    // Show success message
    if (newStatus === 'interested') {
      setSnackbarMessage('Added to your interested events');
    } else if (newStatus === 'going') {
      setSnackbarMessage('You\'re now going to this event!');
    } else {
      setSnackbarMessage('Removed from event');
    }
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  }, [onParticipationChange]);

  // Open share menu
  const handleOpenShareMenu = useCallback((event) => {
    setAnchorShareMenu(event.currentTarget);
  }, []);

  // Close share menu
  const handleCloseShareMenu = useCallback(() => {
    setAnchorShareMenu(null);
  }, []);

  // Handle sharing to specific platform
  const handleShareTo = useCallback((platform) => {
    const eventUrl = window.location.href;
    const shareTitle = eventTitle || 'Check out this event';

    let shareUrl = '';

    switch(platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(eventUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(eventUrl)}&text=${encodeURIComponent(shareTitle)}`;
        break;
      case 'line':
        shareUrl = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(eventUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareTitle + ' ' + eventUrl)}`;
        break;
      default:
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }

    handleCloseShareMenu();
  }, [eventTitle, handleCloseShareMenu]);

  // Copy event link to clipboard
  const handleCopyLink = useCallback(() => {
    const eventUrl = window.location.href;

    navigator.clipboard.writeText(eventUrl)
      .then(() => {
        setSnackbarMessage('Event link copied to clipboard');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      })
      .catch(err => {
        console.error('Failed to copy link:', err);
        setSnackbarMessage('Failed to copy link');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  }, []);

  // Close snackbar
  const handleCloseSnackbar = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  // If we don't have an eventId, don't render anything
  if (!eventId) {
    return null;
  }

  // Check if we have language versions loaded (show buttons even with just 1 version)
  const hasLanguageVersions = languageVersions.length > 0;

  // Debug logging for render
  console.log('🎨 EventActionButtons render debug:', {
    eventId,
    currentLanguage,
    languageVersionsCount: languageVersions.length,
    hasLanguageVersions,
    loadingLanguages,
    eventData: eventData ? {
      language: eventData.language,
      master_event_id: eventData.master_event_id,
      event_name: eventData.event_name
    } : 'null'
  });

  return (
    <>
      <ActionButtonsContainer>
        {/* LEFT SIDE: Participation buttons for authenticated users */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isAuthenticated && getAuthHeaders && (
            <ParticipationButton
              eventId={eventId}
              getAuthHeaders={getAuthHeaders}
              onParticipationChange={handleParticipationChange}
              size="medium"
            />
          )}
        </Box>

        {/* RIGHT SIDE: Share, Copy, and Language buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Share Button */}
          <Tooltip title="Share event">
            <IconButton
              aria-label="Share event"
              onClick={handleOpenShareMenu}
              sx={{
                backgroundColor: '#f8fafc',
                color: '#666666',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: '#e8f5fd',
                  color: '#3a7bd5',
                },
              }}
            >
              <ShareIcon />
            </IconButton>
          </Tooltip>
          
          {/* Copy Link Button */}
          <Tooltip title="Copy event link">
            <IconButton
              aria-label="Copy event link"
              onClick={handleCopyLink}
              sx={{
                backgroundColor: '#f8fafc',
                color: '#666666',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: '#e8f5fd',
                  color: '#3a7bd5',
                },
              }}
            >
              <LinkIcon />
            </IconButton>
          </Tooltip>

          {/* Language switching buttons - NOW AT THE RIGHT END */}
          {hasLanguageVersions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', ml: 1 }}>
              {languageOptions.map((option) => {
                const isAvailable = languageVersions.some(version => version.language === option.code);
                const isActive = currentLanguage === option.code;
                
                return (
                  <Tooltip 
                    key={option.code} 
                    title={isAvailable ? `Switch to ${option.code} version` : `${option.code} version not available`}
                    arrow
                  >
                    <span>
                      <FlagButton
                        onClick={() => isAvailable ? handleLanguageChange(option.code) : null}
                        disabled={loadingLanguages || isActive || !isAvailable}
                        isActive={isActive}
                        aria-label={`Switch to ${option.code} language`}
                        sx={{
                          opacity: isAvailable ? 1 : 0.3,
                          cursor: isAvailable ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <img src={option.flag} alt={option.code} width={24} />
                      </FlagButton>
                    </span>
                  </Tooltip>
                );
              })}
            </Box>
          )}
        </Box>

        {/* DEBUG: Temporary debug info - remove after testing */}
        {process.env.NODE_ENV === 'development' && (
          <Box sx={{ 
            position: 'absolute', 
            top: '100%', 
            left: 0, 
            right: 0,
            p: 1, 
            bgcolor: '#f0f0f0', 
            fontSize: '10px',
            borderRadius: 1,
            zIndex: 1000
          }}>
            Debug: versions={languageVersions.length}, hasVersions={hasLanguageVersions ? 'true' : 'false'}, 
            loading={loadingLanguages ? 'true' : 'false'}, lang={currentLanguage}
          </Box>
        )}
        
        {/* Share Menu */}
        <Menu
          anchorEl={anchorShareMenu}
          open={Boolean(anchorShareMenu)}
          onClose={handleCloseShareMenu}
        >
          <MenuItem onClick={() => handleShareTo('facebook')}>Facebook</MenuItem>
          <MenuItem onClick={() => handleShareTo('twitter')}>Twitter</MenuItem>
          <MenuItem onClick={() => handleShareTo('line')}>Line</MenuItem>
          <MenuItem onClick={() => handleShareTo('whatsapp')}>WhatsApp</MenuItem>
        </Menu>
      </ActionButtonsContainer>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EventActionButtons;